import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = [
        'couponInput', 'orderContainer', 'appliedCouponContainer',
        'appliedCouponPrice', 'appliedCouponTitle', 'appliedCouponLabel', 'total',
        'appliedCouponMessage', 'applyBtn', 'appliedCouponCodeInput', 'orderCard',
    ];

    static values = {
        invalid: String,
        successGift: String,
        successDiscount: String,
        giftLabel: String,
        discountLabel: String,
        couponUrl: { type: String, default: '/wp-json/api/checkout/get-coupon-price' },
    };

    connect() {
        this.originalTotal   = 0;
        this.appliedDiscount = 0;
        this.discountActive  = false;
    }

    showOrderLoading() {
        if (this.hasOrderCardTarget) {
            this.orderCardTarget.classList.add('checkout-order-loading');
        }
    }

    hideOrderLoading() {
        if (this.hasOrderCardTarget) {
            this.orderCardTarget.classList.remove('checkout-order-loading');
        }
    }

    clearFieldErrors() {
        this.element.querySelectorAll('.coupon-field-error').forEach(el => el.remove());
        this.element.querySelectorAll('.input.is-invalid-coupon').forEach(el => {
            el.classList.remove('is-invalid-coupon');
        });
    }

    displayFieldErrors(errors) {
        this.clearFieldErrors();

        for (const [dotKey, message] of Object.entries(errors)) {
            const parts     = dotKey.split('.');
            const inputName = parts.shift() + parts.map(p => `[${p}]`).join('');

            const input = this.element.querySelector(`[name="${inputName}"]`);
            if (!input) continue;

            const formField = input.closest('.form-field');
            if (!formField) continue;

            input.classList.add('is-invalid-coupon');

            const errorEl       = document.createElement('label');
            errorEl.className   = 'form-field-validation coupon-field-error mt-1';
            errorEl.setAttribute('role', 'alert');
            errorEl.textContent = Array.isArray(message) ? message[0] : message;

            formField.appendChild(errorEl);
        }

        const firstError = this.element.querySelector('.coupon-field-error');
        if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    async calculate({ isRecalculation = false } = {}) {
        const couponCode = this.couponInputTarget.value.trim();
        if (!couponCode) return;

        if (!isRecalculation) this.applyBtnTarget.classList.add('is-loading');
        this.showOrderLoading();
        this.appliedCouponMessageTarget.classList.add('d-none');
        this.clearFieldErrors();

        const url = this.couponUrlValue;
        const formData = new FormData(this.element);
        formData.set('coupon_code', couponCode);
        formData.set('applied_coupon_code', couponCode);

        const body = new URLSearchParams();
        for (const [key, value] of formData.entries()) {
            body.append(key, value);
        }

        try {
            const response = await fetch(url, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    body.toString(),
            });

            if (!response.ok) {
                try {
                    const errorData = await response.json();
                    if (errorData.errors && Object.keys(errorData.errors).length) {
                        this.displayFieldErrors(errorData.errors);
                        this.showValidationMessage(errorData.message);
                        return;
                    }
                } catch {}

                if (!isRecalculation) this.styleAppliedCouponMessageError();
                return;
            }

            const data = await response.json();

            if (data.type === 'validation') {
                if (data.errors && Object.keys(data.errors).length) {
                    this.displayFieldErrors(data.errors);
                }
                this.showValidationMessage(data.message);
                return;
            }

            if (data.type === 'form_error') {
                this.showValidationMessage(data.message);
                return;
            }

            if (data.type === 'percentage' && data.new_total != null) {
                this.addPercentageDiscountToCheckout(couponCode, parseFloat(data.new_total));
                this.discountActive = true;
                return;
            }

            if (data.type === 'invalid' || data.status === 'error' || data.message || data.error) {
                if (!isRecalculation) this.styleAppliedCouponMessageError(data.error || data.message);
                return;
            }

            if (data.coupon_worth?.balance) {
                this.addCouponToCheckout(couponCode, parseFloat(data.coupon_worth.balance));
                this.discountActive = true;
                return;
            }

            if (!isRecalculation) this.styleAppliedCouponMessageError();

        } catch (error) {
            if (!isRecalculation) this.styleAppliedCouponMessageError();
        } finally {
            this.applyBtnTarget.classList.remove('is-loading');
            this.hideOrderLoading();
        }
    }

    async recalculateAfterRemoval() {
        const couponCode = this.appliedCouponCodeInputTarget.value;
        if (!couponCode) return;

        this.originalTotal   = 0;
        this.appliedDiscount = 0;
        this.discountActive  = false;

        this.couponInputTarget.value = couponCode;

        await this.calculate({ isRecalculation: true });

        if (!this.discountActive) {
            this.appliedCouponContainerTarget.classList.add('d-none');
            this.appliedCouponCodeInputTarget.value = '';
            this.couponInputTarget.value = '';
        }
    }

    showValidationMessage(message) {
        this.appliedCouponMessageTarget.classList.remove('d-none', 'text-positive');
        this.appliedCouponMessageTarget.classList.add('text-negative');
        this.appliedCouponMessageTarget.textContent = message ||
            'Prieš taikant nuolaidos kodą, užpildykite visus privalomus laukus.';
    }

    addPercentageDiscountToCheckout(couponCode, newTotal) {
        this.appliedCouponLabelTarget.textContent = this.discountLabelValue || 'Nuolaidos kodas';

        if (this.originalTotal === 0) {
            this.originalTotal = parseFloat(
                this.totalTarget.textContent.replace(/[^0-9.-]+/g, '')
            ) || 0;
        }

        this.appliedDiscount = Math.max(0, this.originalTotal - newTotal);

        this.appliedCouponTitleTarget.textContent = couponCode;
        this.appliedCouponCodeInputTarget.value   = couponCode;
        this.appliedCouponPriceTarget.textContent = `−${this.appliedDiscount.toFixed(2)} €`;
        this.appliedCouponContainerTarget.classList.remove('d-none');
        this.totalTarget.textContent = `${newTotal.toFixed(2)} €`;

        this.styleAppliedCouponMessageSuccess(this.successDiscountValue || 'Nuolaidos kodas pritaikytas');
    }

    addCouponToCheckout(couponCode, discountAmount) {
        this.appliedCouponLabelTarget.textContent = this.giftLabelValue || 'Dovanų kuponas';

        if (this.originalTotal === 0) {
            this.originalTotal = parseFloat(
                this.totalTarget.innerHTML.replace(/[^0-9.-]+/g, '')
            );
        }

        this.appliedDiscount = discountAmount;

        const displayed = Math.min(discountAmount, this.originalTotal);
        this.appliedCouponTitleTarget.innerHTML     = couponCode;
        this.appliedCouponCodeInputTarget.value     = couponCode;
        this.appliedCouponPriceTarget.innerHTML     = `−${displayed.toFixed(2)} €`;
        this.appliedCouponContainerTarget.classList.remove('d-none');
        this.totalTarget.innerHTML = `${Math.max(0, this.originalTotal - discountAmount).toFixed(2)} €`;

        this.styleAppliedCouponMessageSuccess(this.successGiftValue || 'Dovanų kuponas pritaikytas');
    }

    removeCoupon(event) {
        event.preventDefault();
        this.discountActive = false;
        this.appliedCouponContainerTarget.classList.add('d-none');
        this.appliedCouponCodeInputTarget.value = '';

        if (this.originalTotal > 0) {
            this.totalTarget.textContent = `${this.originalTotal.toFixed(2)} €`;
        }

        this.originalTotal   = 0;
        this.appliedDiscount = 0;

        if (this.hasCouponInputTarget) {
            this.couponInputTarget.value = '';
        }
    }

    styleAppliedCouponMessageError(message = null) {
        this.appliedCouponMessageTarget.classList.remove('d-none', 'text-positive');
        this.appliedCouponMessageTarget.classList.add('text-negative');
        this.appliedCouponMessageTarget.innerHTML = message || this.invalidValue;
    }

    styleAppliedCouponMessageSuccess(message) {
        this.appliedCouponMessageTarget.classList.remove('d-none', 'text-negative');
        this.appliedCouponMessageTarget.classList.add('text-positive');
        this.appliedCouponMessageTarget.innerHTML = message;
    }
}
